import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Grid, Typography } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import InfoIcon from '@material-ui/icons/Info';
import CommonDataGrid from '../../../shared/components/data-grid';
import { RMSConfirmationDialog } from '../../../shared/components/dialog';
import RMSCommonDialog from '../../../shared/components/dialog/RMSCommonDialog';
import { RMSButton } from '../../../shared/components/buttons';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import { notify } from 'reducers/NotifierReducer';
import { getFullPermissions } from 'reducers/PermissionsReducer';
import useConfirmDialog from '../../../shared/utility/useConfirmDialog';
import {
  BULLETIN_GRID_COLUMNS,
  BULLETIN_DETAIL_FIELDS,
} from '../../constants/personBulletinFormConstants';
import PersonBulletinDialog from './PersonBulletinDialog';
import { getPersonBulletins, getBulletin, deleteBulletin } from '../../services/userService';

const PersonBulletinsTab = ({ personId, onBulletinsChanged }) => {
  const dispatch = useDispatch();
  const bulletinPermission = getFullPermissions('cad', 'Alerts', 'any');
  const [loading, setLoading] = useState(false);
  const [bulletins, setBulletins] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editingBulletin, setEditingBulletin] = useState(null);
  const [detailTarget, setDetailTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();

  const loadBulletins = useCallback(async () => {
    if (!personId) return;

    await executeApiAction({
      setLoading,
      dispatch,
      action: () => getPersonBulletins(personId),
      onSuccess: (data) => setBulletins(data || []),
    });
  }, [dispatch, personId]);

  useEffect(() => {
    loadBulletins();
  }, [loadBulletins]);

  const handleAdd = useCallback(() => {
    setEditingBulletin(null);
    setFormOpen(true);
  }, []);

  const handleEdit = useCallback(
    async (row) => {
      await executeApiAction({
        setLoading,
        dispatch,
        action: () => getBulletin(row.ptsAlertID),
        onSuccess: (data) => {
          setEditingBulletin(data);
          setFormOpen(true);
        },
      });
    },
    [dispatch]
  );

  const handleFormClose = useCallback(() => {
    setFormOpen(false);
    setEditingBulletin(null);
  }, []);

  const handleSaved = useCallback(() => {
    handleFormClose();
    loadBulletins();
    onBulletinsChanged?.();
  }, [handleFormClose, loadBulletins, onBulletinsChanged]);

  const handleViewDetails = useCallback((row) => setDetailTarget(row), []);
  const handleCloseDetails = useCallback(() => setDetailTarget(null), []);

  const handleDelete = useCallback(
    (row) => {
      requestConfirm(
        'Are you sure you want to delete this bulletin?',
        async () => {
          await executeApiAction({
            setLoading: setDeleting,
            dispatch,
            action: () => deleteBulletin(row.ptsAlertID),
            onSuccess: () => {
              dispatch(notify('Bulletin deleted successfully', 'success'));
              loadBulletins();
              onBulletinsChanged?.();
            },
          });
        },
        'Delete Bulletin'
      );
    },
    [requestConfirm, dispatch, loadBulletins, onBulletinsChanged]
  );

  return (
    <Box className="person-names-tab__grid-wrap">
      <Box className="person-addresses-tab__toolbar">
        <RMSButton
          label="Add Bulletin"
          prefixIcon={<AddIcon />}
          onClick={handleAdd}
          disabled={!bulletinPermission.Create}
        />
      </Box>

      <CommonDataGrid
        rows={bulletins}
        columns={BULLETIN_GRID_COLUMNS}
        getRowId={(row) => row.ptsAlertID}
        loading={loading}
        autoHeight
        toolbar={{
          showSearch: true,
          showColumnSelector: true,
          showFilter: true,
          showDensity: true,
          showExport: true,
          printTitle: 'Bulletins Records',
        }}
        actions={{
          edit: { visible: true, onClick: handleEdit, disabled: !bulletinPermission.Edit },
          delete: { visible: true, onClick: handleDelete, disabled: !bulletinPermission.Delete },
          custom: [
            {
              icon: <InfoIcon />,
              tooltip: 'View Details',
              onClick: handleViewDetails,
            },
          ],
        }}
      />

      {formOpen && (
        <PersonBulletinDialog
          open={formOpen}
          personId={personId}
          bulletin={editingBulletin}
          onClose={handleFormClose}
          onSaved={handleSaved}
        />
      )}

      {detailTarget && (
        <RMSCommonDialog
          open={Boolean(detailTarget)}
          title={detailTarget.AlertType || 'Bulletin Details'}
          onClose={handleCloseDetails}
          maxWidth="sm"
          fullWidth
          saveButton={{ show: false }}
          cancelButton={{
            show: true,
            type: 'simple',
            label: 'Close',
            variant: 'outlined',
            color: 'secondary',
          }}>
          <Grid container spacing={1}>
            {BULLETIN_DETAIL_FIELDS.map(({ field, label, render }) => (
              <Grid item xs={6} key={field}>
                <Typography variant="caption" color="textSecondary" component="div">
                  {label}
                </Typography>
                <Typography variant="body2">
                  {render ? render(detailTarget[field]) : detailTarget[field] || ''}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </RMSCommonDialog>
      )}

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
          loading={deleting}
        />
      )}
    </Box>
  );
};

export default PersonBulletinsTab;
